/**
 * Diil → OnePlaceHR dashboard intake.
 *
 * The "Холбогдох" (Contact) and "Санал хүсэлт" (Feedback) forms post directly to
 * the internal OnePlaceHR dashboard's public endpoint. This deliberately does NOT
 * go through Diil's own hrSystemServer / aichatbot backends — it is a pure
 * cross-origin fetch, so it cannot introduce any error into Diil's backends.
 *
 * The target URL is configurable via REACT_APP_DIIL_REPORT_URL. Update the
 * fallback below if the dashboard's production domain differs.
 */

const DIIL_REPORT_URL =
  process.env.REACT_APP_DIIL_REPORT_URL ||
  "https://internal.diil.work/api/diil-report";

const VIDEO_ORDER_URL =
  process.env.REACT_APP_DIIL_VIDEO_ORDER_URL ||
  "https://internal.diil.work/api/diil-video-order";

/**
 * @param {Object} payload
 * @param {"contact"|"feedback"} payload.source
 * @param {string} [payload.topic]    collaboration | ad | other
 * @param {string} [payload.name]
 * @param {string} [payload.contact]  email or phone
 * @param {string} [payload.message]
 * @param {string} [payload.extra]
 * @returns {Promise<{ok: boolean, id?: string, error?: string}>}
 */
/**
 * CV rating (1-5) + optional comment, submitted from the post-CV-wizard modal.
 * Shares the diil-report intake so the dashboard has one inbound pipeline; the
 * `source` field is what separates ratings from contact/feedback messages.
 *
 * @param {Object} payload
 * @param {number} payload.rating   1..5
 * @param {string} [payload.comment]
 * @param {string} [payload.context] what was rated (e.g. "cv")
 * @returns {Promise<{ok: boolean, id?: string, error?: string}>}
 */
export async function sendCvRating({ rating, comment = "", context = "cv" }) {
  return sendReport({
    source: "cv_rate",
    rating,
    topic: context,
    message: comment,
    origin: typeof window !== "undefined" ? window.location.href : "",
  });
}

export async function sendDiilReport(payload) {
  return sendReport({
    source: payload.source === "contact" ? "contact" : "feedback",
    topic: payload.topic || "",
    name: payload.name || "",
    contact: payload.contact || "",
    message: payload.message || "",
    extra: payload.extra || "",
    origin: typeof window !== "undefined" ? window.location.href : "",
  });
}

/**
 * "Бичлэг захиалах" order from the job-posting popup (VideoOrderCard). Has its own
 * endpoint on the dashboard, separate from diil-report, because it carries structured
 * company fields the dashboard lists as video orders rather than as free-text messages.
 *
 * Until that endpoint is live the order falls back to diil-report (source "contact",
 * topic "video_order") with the same fields packed into `message`, so no order is
 * lost while the dashboard catches up.
 *
 * @param {Object} payload
 * @param {string} payload.hrName    name of the HR contact person
 * @param {string} payload.activity  Үйл ажиллагаа
 * @param {string} payload.email
 * @param {string} payload.phone
 * @param {string} payload.position  which job will be advertised
 * @param {string} payload.about     detailed company introduction
 * @param {string} [payload.company] company name, when the wizard already knows it
 * @returns {Promise<{ok: boolean, id?: string, error?: string}>}
 */
export async function sendVideoOrder(payload) {
  const body = {
    source: "video_order",
    company: (payload.company || "").trim(),
    hrName: payload.hrName.trim(),
    activity: payload.activity.trim(),
    email: payload.email.trim(),
    phone: payload.phone.trim(),
    position: payload.position.trim(),
    about: payload.about.trim(),
    origin: typeof window !== "undefined" ? window.location.href : "",
  };
  const res = await postJson(VIDEO_ORDER_URL, body);
  if (res.ok) return res;

  console.error("[sendVideoOrder] video-order endpoint failed, using diil-report:", res.error);
  return sendReport({
    source: "contact",
    topic: "video_order",
    name: body.hrName,
    contact: `${body.email} / ${body.phone}`,
    message: [
      `Компани: ${body.company}`,
      `HR-ын нэр: ${body.hrName}`,
      `Үйл ажиллагаа: ${body.activity}`,
      `Ажлын байр: ${body.position}`,
      `Танилцуулга: ${body.about}`,
    ].join("\n"),
    extra: "",
    origin: body.origin,
  });
}

// Single POST path shared by every diil-report intake type.
function sendReport(body) {
  return postJson(DIIL_REPORT_URL, body);
}

async function postJson(url, body) {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) return { ok: true, id: data.id };
    return { ok: false, error: data.error || `HTTP ${res.status}` };
  } catch (err) {
    // Network/CORS failure must stay contained — surfaced to the UI, never thrown.
    return { ok: false, error: err?.message || "network_error" };
  }
}
